import React from "react";
import {useSelector} from "react-redux";

import {Translation as T, useTranslation} from "@awe-web/shared/lib";
import {getCreditCards} from "@awe-web/shared/lib";

import {TypeArea} from "../../../components/layout.js";
import {Row, Col} from "../../../components/grid.js";
import {
  ScreenSection,
  ScreenSectionHeading,
  ScreenSubsection
} from "../../../components/screen.js";
import {
  FormikLabeledCheckbox,
  Button,
  RequiredNotice,
  FormikErrorMessage,
  ScrollToError,
  FormikLabeledField,
  FormikTextareaWithCharacterCount
} from "../../../components/form.js";
import {LtrMonthlyTotalPrice, LtrOneTimeTotalPrice} from "../../../rent_contracts/components/price";
import ActivatePromocode from "../../../rent_contracts/components/activate_promocode";
import {ProvisioningDetails, ReturnDetails} from "../../../rent_contracts/components/rent_contract";
import {AddCreditCard, CreditCardRadioButtons} from "../../../user/components/credit_cards.jsx";
import TranslationWithCmsLink from "../../../l10n/components/translation_with_cms_link";
import {getCreditCardsEnabled} from "../../../config/store.js";

const PROVISIONING_DETAILS = "ALG0000095";
const RETURN_DETAILS = "FSD0000161";
const RETURN_NOTICE = "ALG0000246";
const TEXTBOX_LABEL = "ALG0000104";
const TEXTBOX_PLACEHOLDER = "ALG0000105";
const COSTS = "FSD0000163";
const SELECT_CREDIT_CARD = "FSD0000170";
const ADD_CREDIT_CARD = "FSD0000222";
const I_ACCEPT_THE_TOS = "FSD0000286";
const I_ACCEPT_THE_PRIVACY_POLICY = "FSD0000287";
const SUBMIT_RESERVATION = "FSD0000174";
const NOTICE = "FSD0000209";
const PRICES_NOTICE = "ALG0000244";
const ADDITIONAL_INFORMATION = "FSD0000175";
const INCLUDING_VAT = "FSD0000263";

export default function SubmitRentContractForm({handleSubmit}) {
  const {translate} = useTranslation();

  const creditCardsEnabled = useSelector((state) => getCreditCardsEnabled(state));
  const creditCards = useSelector((state) => getCreditCards(state));

  return (
    <form onSubmit={handleSubmit}>
      <ScrollToError />

      <ScreenSection main>
        <TypeArea className="b2c-ltr-submit-rent-contract__type-area">
          <Row>
            <Col tablet={{cols: 4}} desktop={{cols: 4, offset: 2}}>
              <ScreenSubsection className="b2c-ltr-submit-rent-contract__type-area__mobile">
                <ScreenSectionHeading light>
                  <T code={PROVISIONING_DETAILS} />
                </ScreenSectionHeading>
                <ProvisioningDetails reservationPath="B2C" />
              </ScreenSubsection>

              <ScreenSubsection className="b2c-ltr-submit-rent-contract__type-area__mobile">
                <ScreenSectionHeading light>
                  <T code={RETURN_DETAILS} />
                </ScreenSectionHeading>
                <ReturnDetails />
                <div className="b2c-ltr-submit-rent-contract__return-notice">
                  <T code={RETURN_NOTICE} />
                </div>
              </ScreenSubsection>

              <ScreenSubsection className="b2c-ltr-submit-rent-contract__type-area__mobile">
                <FormikLabeledField
                  name="contractNote"
                  as={FormikTextareaWithCharacterCount}
                  getFieldLabel={() => translate(TEXTBOX_LABEL)}
                  maxLength={500}
                  placeholder={translate(TEXTBOX_PLACEHOLDER)}
                />
              </ScreenSubsection>
            </Col>

            <Col tablet={{cols: 4}} desktop={{cols: 4}}>
              {creditCardsEnabled && (
                <ScreenSubsection className="b2c-ltr-submit-rent-contract__type-area__mobile">
                  <ScreenSectionHeading light>
                    <T code={SELECT_CREDIT_CARD} />
                  </ScreenSectionHeading>
                  <CreditCardRadioButtons
                    name="creditCardId"
                    creditCards={creditCards.toJS()}
                  />
                  <FormikErrorMessage name="creditCardId" />
                  <T className="b2c-ltr-submit-rent-contract__add-credit-card-heading" code={ADD_CREDIT_CARD} />
                  <AddCreditCard />
                </ScreenSubsection>
              )}

              <ScreenSubsection>
                <ScreenSectionHeading light className="b2c-ltr-submit-rent-contract__type-area__mobile">
                  <T code={COSTS} />
                </ScreenSectionHeading>

                <ActivatePromocode className="b2c-ltr-submit-rent-contract__box"/>

                <LtrMonthlyTotalPrice
                  className="b2c-ltr-submit-rent-contract__box"
                  descriptionHint={translate(INCLUDING_VAT)}
                />

                <LtrOneTimeTotalPrice
                  className="b2c-ltr-submit-rent-contract__box"
                  descriptionHint={translate(INCLUDING_VAT)}
                />
              </ScreenSubsection>

              <ScreenSubsection className="b2c-ltr-submit-rent-contract__type-area__mobile">
                <div className="b2c-ltr-submit-rent-contract__checkbox-tos">
                  <FormikLabeledCheckbox name="agbAccepted">
                    <TranslationWithCmsLink
                      className="b2c-ltr-submit-rent-contract__required-checkbox-label checkbox-label"
                      code={I_ACCEPT_THE_TOS}
                      pageName="agb"
                    />
                  </FormikLabeledCheckbox>
                  <FormikErrorMessage name="agbAccepted" />
                </div>
                <div className="b2c-ltr-submit-rent-contract__checkbox-privacy">
                  <FormikLabeledCheckbox name="privacyAccepted">
                    <TranslationWithCmsLink
                      className="b2c-ltr-submit-rent-contract__required-checkbox-label checkbox-label"
                      code={I_ACCEPT_THE_PRIVACY_POLICY}
                      pageName="privacy"
                    />
                  </FormikLabeledCheckbox>
                  <FormikErrorMessage name="privacyAccepted" />
                </div>

                <Button primary continue submit className="b2c-ltr-submit-rent-contract__submit">
                  <T code={SUBMIT_RESERVATION} />
                </Button>
              </ScreenSubsection>
            </Col>
          </Row>
          <Row>
            <Col desktop={{cols: 8, offset: 2}} className="b2c-ltr-submit-rent-contract__type-area__mobile">
              <RequiredNotice className="b2c-ltr-submit-rent-contract__required-notice">
                <T code={NOTICE} />
              </RequiredNotice>
              <div className="b2c-ltr-submit-rent-contract__additional-information"><T code={ADDITIONAL_INFORMATION} /></div>
              <div><T code={PRICES_NOTICE} /></div>
            </Col>
          </Row>
        </TypeArea>
      </ScreenSection>
    </form>
  );
};
